import { z } from 'zod';
import { memberIdSchema } from '../identity/legacy-identity-id';
import { apiEnvelope } from '../envelope';

/**
 * Wire contract for the staff notification bell.
 *
 * Replaces backend2's `care-person-notifications` (DynamoDB behind AppSync) with
 * shapes the Galapagos relational spine can serve. Two properties are structural
 * rather than incidental:
 *
 *   - **`isRead` is a boolean on the wire; the row stores `read_at`.** The
 *     boundary derives one from the other, so storage keeps strictly more
 *     information (when it was read, not merely that it was) while the Atlas
 *     components that already read `isRead` need no change.
 *   - **No recipient identifier appears anywhere.** backend2 puts
 *     `carePersonIdentityId` on every notification and relies on AppSync
 *     resolvers to scope reads. Here the server scopes every read and write to
 *     the `internal_users.id` that `authorizeStaff()` proves, so a caller cannot
 *     pass someone else's recipient: the request schemas are `.strict()` and a
 *     recipient key is not among their fields.
 *
 *     The response side is guaranteed differently, and deliberately so. Response
 *     schemas here are NOT strict (ADR-135 Rule 1): a client that rejects an
 *     unknown key does not stop a server from sending one, it only breaks the
 *     client on the next additive rollout. What keeps a recipient off the wire is
 *     that the projection does not select it — a server-side property, enforced
 *     where the response is built rather than where it is parsed.
 *
 * Paging is a keyset over `(created_at DESC, id DESC)` rather than DynamoDB's
 * opaque `nextToken`, matching the repo's existing list bodies.
 *
 * Strictness is therefore split by direction, not applied uniformly: `.strict()`
 * on every request (the server refusing unexpected input) and on no response
 * (an installed client must tolerate a field a later server adds).
 *
 * Request schemas here describe the DECODED request, not the raw query string:
 * the route layer splits a CSV filter and parses a numeric `limit` before
 * parsing with them. That is the same split `memberListQuerySchema` and its
 * sibling filter validators use today, except the contract names the decoded end
 * state, so the client, the handler and the test hold one type instead of three.
 *
 * This module is schemas only. The `registerApiRoute` contracts that will carry
 * them belong under `apps/api/src/openapi` (ADR-061 Rule 1).
 */

/** Page size when the caller names none — the size the Atlas panel already pages by. */
export const STAFF_NOTIFICATION_PAGE_SIZE_DEFAULT = 20;
/** Hard ceiling on a single notification page. */
export const STAFF_NOTIFICATION_PAGE_SIZE_MAX = 100;
/**
 * Hard ceiling on an inbound cursor.
 *
 * The cursor is the one free-form caller-controlled string this surface takes —
 * everything else is a uuid, a closed enum, or a bounded number. A server-issued
 * keyset position over `(created_at, id)` needs a fraction of this, so the bound
 * costs a caller nothing and keeps an arbitrarily long value from reaching the
 * decoder at all.
 */
export const STAFF_NOTIFICATION_CURSOR_MAX_LENGTH = 512;

/**
 * What a notification is about. One member today; the `as const` tuple feeds
 * both this enum and the table's `enumCheck`, so the constraint and the contract
 * cannot drift (the pattern `notifications/schema.ts` established).
 *
 * Closed on purpose: adding a second kind is a migration plus a derivation rule
 * for when a row of that kind is written, not a reshape of this contract.
 * backend2's `PRODUCT_REQUEST_NEEDS_APPROVAL` and `CONCERNING_MOOD` are
 * deliberately absent — the first has no Galapagos shipments domain, and the
 * second needs a clinical severity rule that does not exist yet.
 *
 * Values are `snake_case` because this tuple is also the STORAGE vocabulary —
 * it feeds `staff_notifications.kind`'s CHECK, where every other governed enum
 * in the schema is snake_case. backend2's wire form is `NEW_MESSAGE_RECEIVED`;
 * translating between the two belongs to the wire contract, never to the column.
 */
/**
 * `product_request` is the NRT slice's kind: a member has asked for nicotine
 * replacement therapy and someone has to look at the queue.
 *
 * Named for the CLASS, not the product. A kind per orderable thing would grow a
 * vocabulary — and a CHECK, and a migration — every time the catalogue does,
 * and a care person triaging their bell does not need `nrt_request` and
 * `lozenge_request` to be different rows. What the notification carries is
 * "there is a product request waiting"; which product is in the queue it links
 * to. ADR-015 also applies: the kind is stored and rendered on a surface that
 * should not name a substance to say a task exists.
 *
 * APPENDED, never inserted. The tuple order is the CHECK's order, and the
 * existing value has shipped — reordering would rewrite a constraint that
 * already applies to live rows.
 *
 * `escalation_taken_over` tells a care person that an escalation they had
 * claimed is now someone else's. Recipient: the person who held the claim.
 * Cause: whoever displaced them, never the recipient themselves (ADR-139
 * Rule 6). `source_ref` is the id of the escalation event that displaced the
 * holder — a `taken_over` event when a colleague takes over, and other event
 * kinds may write it too, so nothing may assume which kind of event it names.
 *
 * Directed, not primary-routed: see `STAFF_NOTIFICATION_DIRECTED_KINDS`.
 *
 * `care_team_assigned` and `care_team_unassigned` tell a care person that their
 * seat on a member's care team started or ended: an open `care_team_assignments`
 * row for them was inserted or closed. Recipient: the person whose seat it is.
 * Cause: the colleague who made the change, or the system for an automatic
 * assignment, never the recipient themselves (ADR-139 Rule 6). A primary or
 * secondary change on a seat the person keeps writes neither. `source_ref` is
 * the assignment id, which an insert mints once and a close ends once. Both are
 * directed too.
 */
export const STAFF_NOTIFICATION_KIND = [
  'new_message_received',
  'product_request',
  'escalation_taken_over',
  'care_team_assigned',
  'care_team_unassigned',
  'escalation_released_by_manager',
  'escalation_assigned',
] as const;
export const staffNotificationKindSchema = z.enum(STAFF_NOTIFICATION_KIND);
export type StaffNotificationKind = z.infer<typeof staffNotificationKindSchema>;

/**
 * The kinds addressed to a named person by the event that writes them, rather
 * than to the member's primary care person.
 *
 * Three things follow for every kind listed here, and each reads this tuple:
 *
 *   - It is written only through `insertDirectedStaffNotification`, inside the
 *     transaction of the action that caused it, which refuses a row whose
 *     actor is its recipient.
 *   - Care-team reassignment never moves it: it is addressed to the person the
 *     event singled out, not to whoever is primary for the member.
 *   - The database refuses to change its recipient. The trigger function
 *     `staff_notifications_directed_recipient_fixed()` carries this list as a
 *     literal, so appending a value here needs a migration that replaces that
 *     function in the same change; a parity test fails until it exists.
 */
export const STAFF_NOTIFICATION_DIRECTED_KINDS = [
  'escalation_taken_over',
  'care_team_assigned',
  'care_team_unassigned',
  'escalation_released_by_manager',
  'escalation_assigned',
] as const satisfies readonly StaffNotificationKind[];
export type StaffNotificationDirectedKind = (typeof STAFF_NOTIFICATION_DIRECTED_KINDS)[number];

/**
 * The kinds the original (unversioned) bell routes serve — frozen.
 *
 * A literal, deliberately NOT derived from `STAFF_NOTIFICATION_KIND`. Those
 * routes parse `kind` as a closed enum on both sides, and an Atlas tab loaded
 * before a deploy throws on a value its bundle has never seen (ADR-135 Rule 3).
 * Appending a storage kind must therefore never widen this set: the server
 * scopes those routes to it, so a row of a later kind is invisible to them
 * rather than fatal to an older client. New kinds reach clients through the v2
 * routes, whose `kind` is an open string.
 */
export const STAFF_NOTIFICATION_LEGACY_KINDS = ['new_message_received', 'product_request'] as const;
export const staffNotificationLegacyKindSchema = z.enum(STAFF_NOTIFICATION_LEGACY_KINDS);
export type StaffNotificationLegacyKind = z.infer<typeof staffNotificationLegacyKindSchema>;

/**
 * A kind as the v2 routes serve it: any non-empty string.
 *
 * No maximum length, on purpose. Storage is `text` under a vocabulary CHECK with
 * no length invariant, so a cap here would let a future, longer stored kind
 * break an already-shipped v2 parser — the very failure the open kind exists to
 * remove. Bounds belong on caller-supplied input (the v2 filter), not on what a
 * client must be able to read.
 */
export const staffNotificationWireKindSchema = z.string().min(1);

/** Most entries a v2 kind filter may carry — generous against the vocabulary, but bounded. */
export const STAFF_NOTIFICATION_V2_KIND_FILTER_MAX_ENTRIES = 32;
/** Longest single entry a v2 kind filter may carry. Request-side only; see `staffNotificationWireKindSchema`. */
export const STAFF_NOTIFICATION_V2_FILTER_KIND_MAX_LENGTH = 64;

/**
 * A kind filter is a set: repeating a kind selects nothing extra, it only
 * inflates whatever `IN (…)` a route builds from it.
 */
function refuseDuplicateKinds(kinds: readonly string[], ctx: z.RefinementCtx): void {
  const seen = new Set<string>();
  for (const [index, kind] of kinds.entries()) {
    if (seen.has(kind)) {
      ctx.addIssue({
        code: 'custom',
        path: [index],
        message: 'duplicate kind — a filter is a set',
      });
    }
    seen.add(kind);
  }
}

/**
 * One count entry per kind. Two entries for the same kind carry two answers to
 * one question, and a reader that sums them and a reader that takes the first
 * disagree — so the shape refuses the ambiguity rather than leaving each
 * consumer to resolve it.
 */
function refuseDuplicateCountKinds(
  counts: { readonly byKind: readonly { readonly kind: string }[] },
  ctx: z.RefinementCtx,
): void {
  const seen = new Set<string>();
  for (const [index, entry] of counts.byKind.entries()) {
    if (seen.has(entry.kind)) {
      ctx.addIssue({
        code: 'custom',
        path: ['byKind', index, 'kind'],
        message: 'duplicate kind — each kind appears at most once',
      });
    }
    seen.add(entry.kind);
  }
}

/**
 * A kind filter — a SET of kinds, not a list of kind-shaped strings.
 *
 * Three properties, each refusing an ambiguity or an unbounded input rather
 * than leaving it for a route to notice:
 *
 *   - **Non-empty.** An explicitly empty array reads as both "everything" and
 *     "nothing" depending on who wrote the caller, and the server must not have
 *     to guess. Omitting the filter is how a caller says "every kind".
 *   - **No longer than the vocabulary.** A filter selects from a closed enum, so
 *     any array longer than the enum has to repeat itself. `abort: true` stops
 *     the duplicate scan below from walking an oversized array of VALID kinds.
 *     It does not stop the enum check: elements are validated before the length
 *     bound, so an oversized array of invalid entries still yields one issue per
 *     entry. The v2 filter gates the length first for exactly that reason.
 *   - **No duplicates.** Repeating a kind selects nothing extra; it only inflates
 *     whatever `IN (…)` a route builds from it.
 */
export const staffNotificationKindFilterSchema = z
  .array(staffNotificationLegacyKindSchema)
  .nonempty()
  .max(STAFF_NOTIFICATION_LEGACY_KINDS.length, { abort: true })
  .superRefine(refuseDuplicateKinds);
export type StaffNotificationKindFilter = z.infer<typeof staffNotificationKindFilterSchema>;

/**
 * One notification as served.
 *
 * A PROJECTION of the `staff_notifications` row, not a mirror of it: the
 * recipient column and `updated_at` are dropped, `read_at` is coerced to the
 * boolean `isRead`, and the two member names are joined in. That is why this is
 * hand-authored rather than derived through `rowResponse` (ADR-066 Rule 3).
 *
 * The Rule 3 drift guard that ties this shape to the table lives in
 * `response.drift.test.ts` beside this module. It states the difference as
 * three named groups — mirrored columns, deliberately withheld columns, and
 * fields that are derived or joined and have no column at all — because a
 * projection is not a mirror and a blind key-set comparison would be either
 * always-red or meaningless.
 *
 * `memberFirstName` / `memberLastName` come from a join and are nullable, so a
 * notification whose member row is gone still renders rather than 500-ing on a
 * non-null assertion. `sourceRef` is the originating entity — a message id for
 * `new_message_received` — and is nullable because a future kind may have none.
 * Null and empty are not the same absence: the table's `UNIQUE (kind, source_ref)`
 * treats `''` as a real ref that a second event would collide with, so the wire
 * accepts a genuine ref or `null` and nothing in between. No message body,
 * conversation id, or participant reaches this surface.
 */
export const staffNotificationSchema = z.object({
  id: z.string().uuid(),
  memberId: memberIdSchema,
  memberFirstName: z.string().nullable(),
  memberLastName: z.string().nullable(),
  kind: staffNotificationLegacyKindSchema,
  sourceRef: z.string().min(1).nullable(),
  isRead: z.boolean(),
  createdAt: z.string().datetime({ offset: true }),
});
export type StaffNotification = z.infer<typeof staffNotificationSchema>;

/**
 * Keyset page body. `nextCursor` is an opaque server-encoded position over
 * `(createdAt, id)` that no client parses, and is `null` — explicitly present,
 * never omitted — on the last page, so a truncated response cannot be mistaken
 * for the end of the list.
 */
export const staffNotificationPageSchema = z.object({
  items: z.array(staffNotificationSchema),
  nextCursor: z.string().nullable(),
});
export type StaffNotificationPage = z.infer<typeof staffNotificationPageSchema>;

/**
 * Unread counts, per kind. Counted over the table's partial index rather than
 * maintained in a counter row — Postgres can `COUNT`, so backend2's counter
 * entity and its whole class of drift bugs are not ported.
 *
 * A kind with nothing unread MAY be omitted; a reader treats a missing kind as
 * zero.
 */
export const staffNotificationUnreadCountSchema = z.object({
  kind: staffNotificationLegacyKindSchema,
  count: z.number().int().nonnegative().safe(),
});
export type StaffNotificationUnreadCount = z.infer<typeof staffNotificationUnreadCountSchema>;

export const staffNotificationUnreadCountsSchema = z
  .object({
    byKind: z.array(staffNotificationUnreadCountSchema),
  })
  .superRefine(refuseDuplicateCountKinds);
export type StaffNotificationUnreadCounts = z.infer<typeof staffNotificationUnreadCountsSchema>;

// ─── Requests ─────────────────────────────────────────────────────────────────

export const staffNotificationListRequestSchema = z
  .object({
    kinds: staffNotificationKindFilterSchema.optional(),
    unreadOnly: z.boolean().optional(),
    cursor: z.string().min(1).max(STAFF_NOTIFICATION_CURSOR_MAX_LENGTH).optional(),
    limit: z
      .number()
      .int()
      .min(1)
      .max(STAFF_NOTIFICATION_PAGE_SIZE_MAX)
      .default(STAFF_NOTIFICATION_PAGE_SIZE_DEFAULT),
  })
  .strict();
export type StaffNotificationListRequest = z.infer<typeof staffNotificationListRequestSchema>;
/**
 * The same request as a CALLER writes it, before `limit`'s default is applied.
 *
 * `z.infer` is the OUTPUT type, so `limit` is required on it — correct for a
 * handler reading a parsed request, wrong for a client building one. Both sides
 * exist, so both are named here rather than left for each consumer to re-derive
 * (and to re-derive differently).
 */
export type StaffNotificationListRequestInput = z.input<typeof staffNotificationListRequestSchema>;

/**
 * Identifies one notification for mark-read / mark-unread. Shared by both so the
 * two routes cannot drift apart on how a row is named.
 */
export const staffNotificationIdParamSchema = z
  .object({
    notificationId: z.string().uuid(),
  })
  .strict();
export type StaffNotificationIdParam = z.infer<typeof staffNotificationIdParamSchema>;

export const staffNotificationMarkAllReadRequestSchema = z
  .object({
    kinds: staffNotificationKindFilterSchema.optional(),
  })
  .strict();
export type StaffNotificationMarkAllReadRequest = z.infer<
  typeof staffNotificationMarkAllReadRequestSchema
>;

/**
 * Mark every notification about one member read — what the panel does when a
 * care person opens that member's profile. `memberId` is the only identity a
 * caller supplies; the recipient is still the proven staff identity.
 */
export const staffNotificationMarkMemberReadRequestSchema = z
  .object({
    memberId: memberIdSchema,
    kinds: staffNotificationKindFilterSchema.optional(),
  })
  .strict();
export type StaffNotificationMarkMemberReadRequest = z.infer<
  typeof staffNotificationMarkMemberReadRequestSchema
>;

// ─── Responses ────────────────────────────────────────────────────────────────

export const staffNotificationListResponseSchema = apiEnvelope(staffNotificationPageSchema);
export type StaffNotificationListResponse = z.infer<typeof staffNotificationListResponseSchema>;

export const staffNotificationUnreadCountsResponseSchema = apiEnvelope(
  staffNotificationUnreadCountsSchema,
);
export type StaffNotificationUnreadCountsResponse = z.infer<
  typeof staffNotificationUnreadCountsResponseSchema
>;

/**
 * A single-row flip returns the whole updated notification rather than an
 * acknowledgement, so an optimistic client reconciles against the canonical row
 * instead of its own guess at what changed.
 */
export const staffNotificationMutationDataSchema = z.object({
  notification: staffNotificationSchema,
});
export type StaffNotificationMutationData = z.infer<typeof staffNotificationMutationDataSchema>;

export const staffNotificationMarkReadResponseSchema = apiEnvelope(
  staffNotificationMutationDataSchema,
);
export type StaffNotificationMarkReadResponse = z.infer<
  typeof staffNotificationMarkReadResponseSchema
>;

export const staffNotificationMarkUnreadResponseSchema = apiEnvelope(
  staffNotificationMutationDataSchema,
);
export type StaffNotificationMarkUnreadResponse = z.infer<
  typeof staffNotificationMarkUnreadResponseSchema
>;

/**
 * A bulk flip reports how many rows it changed — the number the panel's toast
 * renders. Named rather than a bare integer because the ADR-002 envelope's
 * `data` carries an object.
 */
export const staffNotificationBulkMutationDataSchema = z.object({
  updatedCount: z.number().int().nonnegative().safe(),
});
export type StaffNotificationBulkMutationData = z.infer<
  typeof staffNotificationBulkMutationDataSchema
>;

export const staffNotificationMarkAllReadResponseSchema = apiEnvelope(
  staffNotificationBulkMutationDataSchema,
);
export type StaffNotificationMarkAllReadResponse = z.infer<
  typeof staffNotificationMarkAllReadResponseSchema
>;

export const staffNotificationMarkMemberReadResponseSchema = apiEnvelope(
  staffNotificationBulkMutationDataSchema,
);
export type StaffNotificationMarkMemberReadResponse = z.infer<
  typeof staffNotificationMarkMemberReadResponseSchema
>;

// ─── v2: open kind on the wire ────────────────────────────────────────────────
//
// The same six routes under `/v2`, differing from the shapes above in exactly
// one respect: `kind` is an open string on every response, and the request
// filter ignores kinds the server does not know instead of rejecting them. That
// is what makes adding a kind a one-line storage change rather than a breaking
// API change — a client skips a kind it cannot render instead of failing the
// whole list. Storage stays closed (`STAFF_NOTIFICATION_KIND`).

/**
 * A v2 kind filter: a bounded, non-empty SET of non-empty strings, none of which
 * has to be a kind this server knows.
 *
 * The length is gated BEFORE any element is validated. Chaining `.max(…, {
 * abort: true })` onto an array of strings does not do that in this Zod: the
 * elements are checked first, so 1,000 empty strings answer with 1,001 issues —
 * the allocation the bound exists to avoid. Piping from an unvalidated array
 * yields exactly one.
 */
export const staffNotificationV2KindFilterSchema = z
  .array(z.unknown())
  .max(STAFF_NOTIFICATION_V2_KIND_FILTER_MAX_ENTRIES, { abort: true })
  .pipe(
    z
      .array(z.string().min(1).max(STAFF_NOTIFICATION_V2_FILTER_KIND_MAX_LENGTH))
      .nonempty()
      .superRefine(refuseDuplicateKinds),
  );
export type StaffNotificationV2KindFilter = z.infer<typeof staffNotificationV2KindFilterSchema>;

/**
 * DOCUMENTATION ONLY — never validates a request.
 *
 * The OpenAPI generator renders a `.pipe` from `z.unknown()` as an array of
 * anything, dropping the string type, both length bounds and `minItems`. This
 * twin states the same constraints in a shape the generator can draw, built
 * from the same named constants so the two cannot describe different limits;
 * a test holds their verdicts equal.
 */
export const staffNotificationV2KindFilterDocSchema = z
  .array(z.string().min(1).max(STAFF_NOTIFICATION_V2_FILTER_KIND_MAX_LENGTH))
  .min(1)
  .max(STAFF_NOTIFICATION_V2_KIND_FILTER_MAX_ENTRIES);

export const staffNotificationV2Schema = staffNotificationSchema.extend({
  kind: staffNotificationWireKindSchema,
});
export type StaffNotificationV2 = z.infer<typeof staffNotificationV2Schema>;

export const staffNotificationV2PageSchema = z.object({
  items: z.array(staffNotificationV2Schema),
  nextCursor: z.string().nullable(),
});
export type StaffNotificationV2Page = z.infer<typeof staffNotificationV2PageSchema>;

export const staffNotificationV2UnreadCountSchema = staffNotificationUnreadCountSchema.extend({
  kind: staffNotificationWireKindSchema,
});
export type StaffNotificationV2UnreadCount = z.infer<typeof staffNotificationV2UnreadCountSchema>;

export const staffNotificationV2UnreadCountsSchema = z
  .object({
    byKind: z.array(staffNotificationV2UnreadCountSchema),
  })
  .superRefine(refuseDuplicateCountKinds);
export type StaffNotificationV2UnreadCounts = z.infer<typeof staffNotificationV2UnreadCountsSchema>;

export const staffNotificationV2ListRequestSchema = staffNotificationListRequestSchema
  .extend({ kinds: staffNotificationV2KindFilterSchema.optional() })
  .strict();
export type StaffNotificationV2ListRequest = z.infer<typeof staffNotificationV2ListRequestSchema>;
export type StaffNotificationV2ListRequestInput = z.input<
  typeof staffNotificationV2ListRequestSchema
>;

export const staffNotificationV2MarkAllReadRequestSchema = z
  .object({
    kinds: staffNotificationV2KindFilterSchema.optional(),
  })
  .strict();
export type StaffNotificationV2MarkAllReadRequest = z.infer<
  typeof staffNotificationV2MarkAllReadRequestSchema
>;

export const staffNotificationV2MarkMemberReadRequestSchema = z
  .object({
    memberId: memberIdSchema,
    kinds: staffNotificationV2KindFilterSchema.optional(),
  })
  .strict();
export type StaffNotificationV2MarkMemberReadRequest = z.infer<
  typeof staffNotificationV2MarkMemberReadRequestSchema
>;

/** DOCUMENTATION ONLY — the generator-drawable twin of the v2 mark-all-read body. */
export const staffNotificationV2MarkAllReadRequestDocSchema = z
  .object({
    kinds: staffNotificationV2KindFilterDocSchema.optional(),
  })
  .strict();

/** DOCUMENTATION ONLY — the generator-drawable twin of the v2 mark-member-read body. */
export const staffNotificationV2MarkMemberReadRequestDocSchema = z
  .object({
    memberId: memberIdSchema,
    kinds: staffNotificationV2KindFilterDocSchema.optional(),
  })
  .strict();

export const staffNotificationV2ListResponseSchema = apiEnvelope(staffNotificationV2PageSchema);
export type StaffNotificationV2ListResponse = z.infer<typeof staffNotificationV2ListResponseSchema>;

export const staffNotificationV2UnreadCountsResponseSchema = apiEnvelope(
  staffNotificationV2UnreadCountsSchema,
);
export type StaffNotificationV2UnreadCountsResponse = z.infer<
  typeof staffNotificationV2UnreadCountsResponseSchema
>;

export const staffNotificationV2MutationDataSchema = z.object({
  notification: staffNotificationV2Schema,
});
export type StaffNotificationV2MutationData = z.infer<typeof staffNotificationV2MutationDataSchema>;

export const staffNotificationV2MarkReadResponseSchema = apiEnvelope(
  staffNotificationV2MutationDataSchema,
);
export type StaffNotificationV2MarkReadResponse = z.infer<
  typeof staffNotificationV2MarkReadResponseSchema
>;

export const staffNotificationV2MarkUnreadResponseSchema = apiEnvelope(
  staffNotificationV2MutationDataSchema,
);
export type StaffNotificationV2MarkUnreadResponse = z.infer<
  typeof staffNotificationV2MarkUnreadResponseSchema
>;
